import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { motion } from 'framer-motion';
import { CARD_CLASS, OMS_COLORS, baseChartOptions, sectionVariants } from '../../dashboard/omsDashTheme';
import { ADMIN, ADMIN_EYEBROW_CLASS } from '../adminTheme';
import { formatNumber } from '../../dashboard/utils/formatNumber';
import type { GrowthPoint } from '../utils/adminMetrics';
import { DeltaBadge } from './adminBits';

interface Props {
  points: GrowthPoint[];
  prevPoints: GrowthPoint[];
  compare: boolean;
  phrase: string;
  loading: boolean;
}

/**
 * Growth — new projects per period (bars) + cumulative portfolio (line),
 * with capacity added and engineers onboarded. Compare mode overlays the
 * previous window's new-project counts as a dashed ghost.
 */
export default function GrowthPanel({ points, prevPoints, compare, phrase, loading }: Props) {
  const totals = points.reduce(
    (acc, p) => {
      acc.newProjects += p.newProjects;
      acc.capacityMW += p.capacityMW;
      acc.engineers += p.engineersOnboarded;
      return acc;
    },
    { newProjects: 0, capacityMW: 0, engineers: 0 }
  );
  const prevTotals = prevPoints.reduce(
    (acc, p) => {
      acc.newProjects += p.newProjects;
      acc.capacityMW += p.capacityMW;
      acc.engineers += p.engineersOnboarded;
      return acc;
    },
    { newProjects: 0, capacityMW: 0, engineers: 0 }
  );

  const series: ApexAxisChartSeries = [
    { name: 'New projects', type: 'column', data: points.map((p) => p.newProjects) },
    { name: 'Cumulative portfolio', type: 'line', data: points.map((p) => p.cumulativeProjects) },
  ];
  if (compare && prevPoints.length > 0) {
    series.push({
      name: `New projects (${phrase.replace('vs ', '')})`,
      type: 'line',
      data: points.map((_, i) => prevPoints[i]?.newProjects ?? 0),
    });
  }

  const options: ApexOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'line', stacked: false },
    colors: [ADMIN.petrol, '#1F5A47', OMS_COLORS.slate],
    stroke: { width: [0, 2.5, 2], curve: 'smooth', dashArray: [0, 0, 6] },
    plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
    xaxis: {
      categories: points.map((p) => p.label),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: OMS_COLORS.caption, fontSize: '10px', fontWeight: 600 }, hideOverlappingLabels: true, rotate: 0 },
    },
    yaxis: [
      {
        title: { text: 'New projects', style: { color: OMS_COLORS.caption, fontSize: '9px', fontWeight: 700 } },
        labels: { formatter: (v: number) => String(Math.round(v)), style: { colors: OMS_COLORS.caption, fontSize: '10px' } },
        min: 0,
      },
      {
        opposite: true,
        title: { text: 'Cumulative', style: { color: OMS_COLORS.caption, fontSize: '9px', fontWeight: 700 } },
        labels: { formatter: (v: number) => String(Math.round(v)), style: { colors: OMS_COLORS.caption, fontSize: '10px' } },
        min: 0,
      },
    ],
    grid: { borderColor: 'rgba(11,59,46,0.08)', xaxis: { lines: { show: false } } },
    legend: { ...baseChartOptions.legend, position: 'bottom' },
    tooltip: { theme: 'light', shared: true, intersect: false },
  };

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={3}
      aria-label="Portfolio growth"
      className={`${CARD_CLASS} p-4`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className={ADMIN_EYEBROW_CLASS}>Growth · portfolio &amp; onboarding</p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] font-semibold" style={{ color: OMS_COLORS.caption }}>
          <span>
            <span className="font-extrabold tabular-nums" style={{ color: OMS_COLORS.tealDark }}>
              {formatNumber(totals.newProjects)}
            </span>{' '}
            new projects <DeltaBadge delta={totals.newProjects - prevTotals.newProjects} phrase={phrase} />
          </span>
          <span>
            <span className="font-extrabold tabular-nums" style={{ color: OMS_COLORS.tealDark }}>
              {totals.capacityMW.toFixed(1)} MWp
            </span>{' '}
            capacity added
          </span>
          <span>
            <span className="font-extrabold tabular-nums" style={{ color: OMS_COLORS.tealDark }}>
              {formatNumber(totals.engineers)}
            </span>{' '}
            engineers onboarded <DeltaBadge delta={totals.engineers - prevTotals.engineers} phrase={phrase} />
          </span>
        </div>
      </div>
      {loading && points.length === 0 ? (
        <div className="mt-3 h-64 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
      ) : (
        <Chart type="line" height={260} options={options} series={series} />
      )}
    </motion.section>
  );
}
